<template>
  <UmisisModal
    ref="launchTaskTemplateModal"
    :title="modalTitle"
    :description="modalDescription"
    @show="onModalShow"
  >
    <div class="flex flex-col gap-4">
      <div v-if="hasVariables" class="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
        <span class="font-medium text-foreground">Step {{ currentStep }} of {{ totalSteps }}</span>
        <span class="text-muted-foreground">{{ currentStep === 1 ? 'Variables' : 'Overrides' }}</span>
      </div>

      <div v-if="currentStep === 1" class="flex flex-col gap-4">
        <Field v-if="hasVariables">
          <FieldLabel>
            Variables
          </FieldLabel>
          <FieldDescription>Provide the values that will replace this template's variable tokens when the task is created.</FieldDescription>
          <TaskTemplateVariablesForm
            v-model="variableValues"
            :variables-schema="variablesSchema"
            :errors="formErrors"
            :disabled="props.isLaunching"
          />
        </Field>

        <div v-else class="rounded-md border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
          No variables are required for this template.
        </div>
      </div>

      <div v-else class="flex flex-col gap-4">
        <Field>
          <FieldLabel for="launch-task-template-user">
            Select user
          </FieldLabel>
          <select
            id="launch-task-template-user"
            v-model.number="selectedUserId"
            class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option :value="null" disabled>
              Choose a user
            </option>
            <option v-for="user in availableUsers" :key="`launch-task-template-user-${user.id}`" :value="user.id">
              {{ user.name }} ({{ user.email }})
            </option>
          </select>
          <FieldDescription v-if="availableUsers.length > 0">
            Select a user other than the template default assignee or yourself.
          </FieldDescription>
          <FieldDescription v-else>
            No other users are currently available.
          </FieldDescription>
          <FieldError v-if="assigneeError">
            {{ assigneeError }}
          </FieldError>
        </Field>

        <Field>
          <FieldLabel for="launch-task-template-priority">
            Priority
          </FieldLabel>
          <select
            id="launch-task-template-priority"
            v-model="selectedPriority"
            class="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="low">
              Low
            </option>
            <option value="standard">
              Standard
            </option>
            <option value="high">
              High
            </option>
          </select>
          <FieldDescription>Starts from the template priority and can be overridden for this launch.</FieldDescription>
          <FieldError v-if="priorityError">
            {{ priorityError }}
          </FieldError>
        </Field>

        <Field>
          <FieldLabel for="launch-task-template-deadline">
            Deadline
          </FieldLabel>
          <Input id="launch-task-template-deadline" v-model="selectedDeadlineValue" type="datetime-local" />
          <FieldDescription>Starts from the template deadline. Leave blank to create the task without a deadline.</FieldDescription>
          <FieldError v-if="deadlineError">
            {{ deadlineError }}
          </FieldError>
        </Field>
      </div>
    </div>

    <template #footer="{ cancel }">
      <div class="flex w-full items-center justify-between gap-3">
        <div class="text-sm text-muted-foreground">
          <span v-if="hasVariables">Overrides are optional for this launch.</span>
        </div>
        <div class="flex items-center gap-2">
          <Button type="button" variant="outline" @click="cancel">
            Cancel
          </Button>
          <Button
            v-if="showBackButton"
            type="button"
            variant="outline"
            :disabled="props.isLaunching"
            @click="goToPreviousStep"
          >
            Back
          </Button>
          <Button
            v-if="showNextButton"
            type="button"
            variant="outline"
            :disabled="props.isLaunching"
            @click="goToNextStep"
          >
            Go to Overrides
          </Button>
          <Button
            type="button"
            :disabled="isSubmitDisabled"
            @click="submitLaunch"
          >
            {{ showNextButton ? 'Launch Directly' : 'Launch' }}
          </Button>
        </div>
      </div>
    </template>
  </UmisisModal>
</template>

<script setup>
import axios from 'axios'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { useModalControl } from '../../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '../../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../../resources/js/components/ui/input/index.js'
import TaskTemplateVariablesForm from '../TaskTemplateVariablesForm.vue'
import {
  buildInitialTaskTemplateVariableValues,
  buildTaskTemplateLaunchVariablesPayload,
} from '../../../utils/taskTemplateVariables.js'
import {
  buildLaunchTaskTemplateDeadlineValue,
  formatLaunchTaskTemplateDeadline,
  normalizeUserId,
  resolveTaskTemplateLaunchAssignee,
} from '../../../utils/launchTaskTemplate.js'

const props = defineProps({
  taskTemplate: {
    type: Object,
    required: true,
  },
  currentUser: {
    type: Object,
    default: null,
  },
  initialUserId: {
    type: Number,
    default: null,
  },
  isLaunching: {
    type: Boolean,
    default: false,
  },
  submitLaunchHandler: {
    type: Function,
    required: true,
  },
})

const sharedUsers = ref([])
const loadingUsers = ref(false)
const hasLoadedUsers = ref(false)
const selectedUserId = ref(null)
const selectedPriority = ref('standard')
const selectedDeadlineValue = ref('')
const variableValues = ref({})
const formErrors = ref({})
const currentStep = ref(1)

const { componentRef: launchTaskTemplateModal, openModal, closeModal } = useModalControl()

const defaultAssignee = computed(() => {
  return resolveTaskTemplateLaunchAssignee(props.taskTemplate, props.currentUser)
})
const defaultAssignedUserId = computed(() => defaultAssignee.value.id)
const currentUserId = computed(() => normalizeUserId(props.currentUser?.id))
const normalizedPriority = computed(() => {
  if (typeof props.taskTemplate?.priority === 'string') {
    return props.taskTemplate.priority
  }

  if (props.taskTemplate?.priority && typeof props.taskTemplate.priority === 'object' && 'value' in props.taskTemplate.priority) {
    return String(props.taskTemplate.priority.value)
  }

  return 'standard'
})
const templateDeadlineValue = computed(() => {
  return buildLaunchTaskTemplateDeadlineValue(props.taskTemplate?.default_deadline_after_minutes)
})
const variablesSchema = computed(() => {
  return Array.isArray(props.taskTemplate?.variables_schema) ? props.taskTemplate.variables_schema : []
})
const excludedUserIds = computed(() => {
  return new Set(
    [defaultAssignedUserId.value, currentUserId.value].filter((userId) => userId !== null)
  )
})
const availableUsers = computed(() => {
  return sharedUsers.value.filter((user) => {
    const normalizedUserId = normalizeUserId(user.id)

    if (normalizedUserId === selectedUserId.value) {
      return true
    }

    return !excludedUserIds.value.has(normalizedUserId)
  })
})
const isOverridesStepValid = computed(() => {
  return props.isLaunching
    || loadingUsers.value
    || !Number.isInteger(selectedUserId.value)
    || selectedUserId.value <= 0
})
const hasVariables = computed(() => variablesSchema.value.length > 0)
const totalSteps = computed(() => hasVariables.value ? 2 : 1)
const modalTitle = computed(() => currentStep.value === 1 ? 'Launch Task Variables' : 'Launch Task Overrides')
const modalDescription = computed(() => {
  return currentStep.value === 1
    ? 'Provide the variable values that will be inserted into the generated task.'
    : 'Pick the assignee and adjust any optional launch overrides before creating the task.'
})
const showBackButton = computed(() => hasVariables.value && currentStep.value > 1)
const showNextButton = computed(() => hasVariables.value && currentStep.value < totalSteps.value)
const isSubmitDisabled = computed(() => {
  if (currentStep.value === 1) {
    return props.isLaunching
  }

  return isOverridesStepValid.value
})
const assigneeError = computed(() => resolveFieldError('assigned_to_user_id'))
const priorityError = computed(() => resolveFieldError('priority'))
const deadlineError = computed(() => resolveFieldError('deadline_at'))

const fetchUsers = async () => {
  if (hasLoadedUsers.value || loadingUsers.value) {
    return
  }

  loadingUsers.value = true

  try {
    const { data } = await axios.get('/api/v1/users', {
      params: {
        not_paginated: true,
        order_by: 'name',
        order_direction: 'asc',
      },
    })

    sharedUsers.value = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
    hasLoadedUsers.value = true
  } catch (error) {
    const errorMessage = error?.response?.data?.message || 'Unable to load users.'
    toast.error(errorMessage)
  } finally {
    loadingUsers.value = false
  }
}

const onModalShow = async () => {
  currentStep.value = 1
  formErrors.value = {}
  selectedUserId.value = Number.isInteger(props.initialUserId) && props.initialUserId > 0
    ? props.initialUserId
    : null
  selectedPriority.value = normalizedPriority.value
  selectedDeadlineValue.value = templateDeadlineValue.value
  variableValues.value = buildInitialTaskTemplateVariableValues(variablesSchema.value)

  await fetchUsers()
}

const resolveFieldError = (key) => {
  const fieldErrors = formErrors.value?.[key]

  if (!Array.isArray(fieldErrors) || fieldErrors.length === 0) {
    return ''
  }

  return typeof fieldErrors[0] === 'string' ? fieldErrors[0] : ''
}

const goToNextStep = () => {
  currentStep.value = Math.min(totalSteps.value, currentStep.value + 1)
}

const goToPreviousStep = () => {
  currentStep.value = Math.max(1, currentStep.value - 1)
}

const submitLaunch = async (event) => {
  event?.preventDefault?.()
  formErrors.value = {}

  if (!Number.isInteger(selectedUserId.value) || selectedUserId.value <= 0) {
    toast.error('Select a valid user.')
    return
  }

  let deadlineAt = null

  if (selectedDeadlineValue.value) {
    const selectedDeadline = new Date(selectedDeadlineValue.value)

    if (Number.isNaN(selectedDeadline.getTime())) {
      toast.error('Please provide a valid deadline.')
      return
    }

    deadlineAt = selectedDeadline.toISOString()
  }

  const selectedUser = sharedUsers.value.find((user) => normalizeUserId(user.id) === selectedUserId.value)
  const formattedDeadline = deadlineAt ? formatLaunchTaskTemplateDeadline(deadlineAt) : null

  try {
    await props.submitLaunchHandler({
      payload: {
        assigned_to_user_id: selectedUserId.value,
        priority: selectedPriority.value,
        deadline_at: deadlineAt,
        variables: buildTaskTemplateLaunchVariablesPayload(variableValues.value),
      },
      targetLabel: selectedUser?.name ?? `User #${selectedUserId.value}`,
      deadlineLabel: formattedDeadline,
    })

    closeModal()
  } catch (error) {
    formErrors.value = error?.response?.data?.errors ?? {}

    if (currentStep.value !== 2 && (
      formErrors.value.assigned_to_user_id
      || formErrors.value.priority
      || formErrors.value.deadline_at
    )) {
      currentStep.value = 2
    }

    if (Object.keys(formErrors.value).length === 0) {
      toast.error(error?.response?.data?.message || 'Unable to launch task template.')
    }
  }
}

defineExpose({
  openModal,
})
</script>
